import { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { translations } from '@/translations/translations';
import type { RecordingSort } from './phrase-recording-stats';

interface Props {
  copy: typeof translations.fr.admin.excelPhrases;
  search: string;
  domain: string;
  domains: string[];
  selectedCount: number;
  isConfirming: boolean;
  isRemoving: boolean;
  isImporting: boolean;
  onSearchChange: (value: string) => void;
  onDomainChange: (value: string) => void;
  recordingSort: RecordingSort;
  onRecordingSortChange: (value: RecordingSort) => void;
  onImport: (file: File) => void;
  onAskRemove: () => void;
  onCancelRemove: () => void;
  onRemove: () => void;
}

const ALL_DOMAINS = '__all__';

export function ExcelPhraseToolbar(props: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { copy } = props;
  const handleFile = (file: File | undefined) => {
    if (file) props.onImport(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-3 rounded-xl border border-border/50 bg-background p-3 shadow-sm">
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_13rem_13rem_auto]">
        <div className="relative">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={props.search} onChange={(event) => props.onSearchChange(event.target.value)} placeholder={copy.search} aria-label={copy.search} className="h-10 pl-9" />
        </div>
        <Select value={props.domain || ALL_DOMAINS} onValueChange={(value) => props.onDomainChange(value === ALL_DOMAINS ? '' : value)}>
          <SelectTrigger className="h-10" aria-label={copy.domain}>
            <SelectValue placeholder={copy.allDomains} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_DOMAINS}>{copy.allDomains}</SelectItem>
            {props.domains.map((domain) => <SelectItem key={domain} value={domain}>{domain}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={props.recordingSort} onValueChange={(value) => props.onRecordingSortChange(value as RecordingSort)}>
          <SelectTrigger className="h-10" aria-label={copy.recordingSort}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="file">{copy.sortFile}</SelectItem>
            <SelectItem value="most">{copy.sortMost}</SelectItem>
            <SelectItem value="least">{copy.sortLeast}</SelectItem>
            <SelectItem value="never">{copy.sortNever}</SelectItem>
          </SelectContent>
        </Select>
        <label className={cn('inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-4 text-xs font-semibold text-foreground hover:bg-muted/60', props.isImporting && 'pointer-events-none opacity-50')}>
          <Icon name={props.isImporting ? 'loader2' : 'upload'} className={cn('h-4 w-4', props.isImporting && 'animate-spin')} />
          {props.isImporting ? copy.importingFile : copy.importFile}
          <input ref={inputRef} type="file" accept=".xlsx,.xls" disabled={props.isImporting} onChange={(event) => handleFile(event.target.files?.[0])} className="sr-only" />
        </label>
      </div>
      <p className="text-xs text-muted-foreground">{copy.fileRequirements}</p>
      {props.selectedCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-primary/[0.06] px-3 py-2">
          <span className="text-xs font-semibold text-foreground">{copy.selected.replace('{count}', String(props.selectedCount))}</span>
          <div className="flex items-center gap-2">
            {props.isConfirming && <button type="button" onClick={props.onCancelRemove} disabled={props.isRemoving} className="text-xs font-semibold text-muted-foreground hover:text-foreground">{copy.cancelSelection}</button>}
            <button type="button" onClick={props.isConfirming ? props.onRemove : props.onAskRemove} disabled={props.isRemoving} className="inline-flex h-8 items-center gap-2 rounded-full bg-destructive px-4 text-xs font-semibold text-destructive-foreground disabled:opacity-50">
              <Icon name={props.isRemoving ? 'loader2' : 'trash2'} className={cn('h-3.5 w-3.5', props.isRemoving && 'animate-spin')} />
              {props.isConfirming ? copy.confirmSelected : copy.removeSelected}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
